import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { RefreshCcwIcon } from 'lucide-react';
import { tenantApi } from '../../lib/tenant-api';
import { toast } from 'react-toastify';

const AdvancedSection: React.FC = () => {
  const [showCacheClearModal, setShowCacheClearModal] = useState(false);

  const clearCacheMutation = useMutation({
    mutationFn: tenantApi.clearAllCache,
    onSuccess: (data) => {
      toast.success(`Successfully cleared cache! ${data.total_keys_cleared} cache entries removed.`);
      setShowCacheClearModal(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to clear cache: ${error.message}`);
      setShowCacheClearModal(false);
    },
  });

  return (
    <>
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Advanced Settings</h3>
          <p className="text-sm text-gray-600 mb-6">
            Manage advanced configurations and system operations for your account.
          </p>
        </div>

        <div className="bg-white border border-gray-200  p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="text-base font-medium text-gray-900 mb-2">Cache Management</h4>
              <p className="text-sm text-gray-600 mb-4">
                Clear all cached data for your account. This is a safe operation that will refresh your AI agent with the latest data.
                You may experience slightly longer load times on your next requests while the cache rebuilds.
              </p>
              <div className="bg-indigo-50 border border-indigo-200  p-3 mb-4">
                <p className="text-xs text-indigo-800">
                  <strong>What gets cleared:</strong> Homepage content, AI prompts, widget configuration, FAQ data, and all other cached information.
                  This does not delete any of your actual data - it only refreshes how it's cached for faster access.
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowCacheClearModal(true)}
            className="inline-flex items-center px-4 py-2 border border-orange-300 shadow-sm text-sm font-medium  text-orange-700 bg-white hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            disabled={clearCacheMutation.isPending}
          >
            <RefreshCcwIcon className={`h-4 w-4 mr-2 ${clearCacheMutation.isPending ? 'animate-spin' : ''}`} />
            {clearCacheMutation.isPending ? 'Clearing Cache...' : 'Clear All Cache'}
          </button>
        </div>
      </div>

      {/* Cache Clear Confirmation Modal */}
      {showCacheClearModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
              onClick={() => !clearCacheMutation.isPending && setShowCacheClearModal(false)}
            ></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white  text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Clear All Cache?
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        This will clear all cached data for your account. This is a safe operation that will refresh your AI agent with the latest data.
                        You may experience slightly longer load times on your next requests.
                      </p>
                      <div className="mt-3 bg-gray-50  p-3">
                        <p className="text-xs text-gray-600">
                          <strong>Note:</strong> This action does not delete any of your data. It only clears the cache to ensure you're seeing the most up-to-date information.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center  border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  onClick={() => clearCacheMutation.mutate()}
                  disabled={clearCacheMutation.isPending}
                >
                  {clearCacheMutation.isPending ? 'Clearing...' : 'Clear Cache'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center  border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowCacheClearModal(false)}
                  disabled={clearCacheMutation.isPending}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdvancedSection;
